import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { ComplianceService } from '@/services/compliance.service';
import { ContractService } from '@/services/contract.service';
import {
  ComplianceStatus,
  AuditStatus,
  IncidentStatus,
  IncidentSeverity,
} from '@/models/compliance.model';
import { format, isAfter, differenceInDays } from 'date-fns';

export const Legal: React.FC = () => {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<'compliance' | 'policies' | 'audits' | 'incidents' | 'baas' | 'contracts'>('compliance');

  // Compliance Records
  const { data: complianceRecords = [], isLoading: complianceLoading } = useQuery({
    queryKey: ['complianceRecords'],
    queryFn: () => ComplianceService.getAllComplianceRecords(),
    enabled: !!profile && activeTab === 'compliance',
  });

  // Policies
  const { data: policies = [], isLoading: policiesLoading } = useQuery({
    queryKey: ['policies'],
    queryFn: () => ComplianceService.getAllPolicies(),
    enabled: !!profile && activeTab === 'policies',
  });

  // Audits
  const { data: audits = [], isLoading: auditsLoading } = useQuery({
    queryKey: ['audits'],
    queryFn: () => ComplianceService.getAllAudits(),
    enabled: !!profile && activeTab === 'audits',
  });

  // Incidents
  const { data: incidents = [], isLoading: incidentsLoading } = useQuery({
    queryKey: ['complianceIncidents'],
    queryFn: () => ComplianceService.getAllIncidents(),
    enabled: !!profile && activeTab === 'incidents',
  });

  // BAAs
  const { data: baas = [], isLoading: baasLoading } = useQuery({
    queryKey: ['baas'],
    queryFn: () => ComplianceService.getAllBAAs(),
    enabled: !!profile && activeTab === 'baas',
  });

  // Contracts
  const { data: contracts = [], isLoading: contractsLoading } = useQuery({
    queryKey: ['contracts'],
    queryFn: () => ContractService.getAllContracts(),
    enabled: !!profile && activeTab === 'contracts',
  });

  const tabs = [
    { id: 'compliance' as const, name: 'Compliance Records', count: complianceRecords.length },
    { id: 'policies' as const, name: 'Policies', count: policies.length },
    { id: 'audits' as const, name: 'Audits', count: audits.length },
    { id: 'incidents' as const, name: 'Incidents', count: incidents.length },
    { id: 'baas' as const, name: 'Business Associate Agreements', count: baas.length },
    { id: 'contracts' as const, name: 'Contracts', count: contracts.length },
  ];

  const getComplianceStatusColor = (status: ComplianceStatus) => {
    const colors: Record<ComplianceStatus, string> = {
      compliant: 'bg-green-100 text-green-800',
      non_compliant: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      expired: 'bg-red-100 text-red-800',
      under_review: 'bg-blue-100 text-blue-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getAuditStatusColor = (status: AuditStatus) => {
    const colors: Record<AuditStatus, string> = {
      scheduled: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getIncidentSeverityColor = (severity: IncidentSeverity) => {
    const colors: Record<IncidentSeverity, string> = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800',
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  const getIncidentStatusColor = (status: IncidentStatus) => {
    const colors: Record<IncidentStatus, string> = {
      reported: 'bg-blue-100 text-blue-800',
      under_investigation: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Calculate stats
  const compliantCount = complianceRecords.filter(r => r.status === ComplianceStatus.compliant).length;
  const nonCompliantCount = complianceRecords.filter(r => r.status === ComplianceStatus.nonCompliant).length;
  const activePolicies = policies.filter(p => p.status === 'active').length;
  const criticalIncidents = incidents.filter(i => i.severity === IncidentSeverity.critical && i.status !== IncidentStatus.closed).length;

  // Upcoming due dates (within 30 days)
  const upcomingDueDates = complianceRecords.filter(r => {
    if (!r.dueDate) return false;
    const daysUntilDue = differenceInDays(r.dueDate, new Date());
    return daysUntilDue >= 0 && daysUntilDue <= 30 && r.status !== ComplianceStatus.compliant;
  }).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Legal & Compliance Management</h1>
        <p className="mt-2 text-gray-600">Manage compliance records, policies, audits, incidents, BAAs, and contracts</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Compliant Records</div>
          <div className="text-2xl font-bold text-green-600">{compliantCount}</div>
          <div className="text-xs text-gray-500 mt-1">Out of {complianceRecords.length} total</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Active Policies</div>
          <div className="text-2xl font-bold text-blue-600">{activePolicies}</div>
          <div className="text-xs text-gray-500 mt-1">{policies.length} total policies</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Upcoming Deadlines</div>
          <div className="text-2xl font-bold text-orange-600">{upcomingDueDates}</div>
          <div className="text-xs text-gray-500 mt-1">Next 30 days</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Critical Incidents</div>
          <div className="text-2xl font-bold text-red-600">{criticalIncidents}</div>
          <div className="text-xs text-gray-500 mt-1">Open critical issues</div>
        </div>
      </div>

      {/* Alert for non-compliant items */}
      {nonCompliantCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {nonCompliantCount} Non-Compliant Record{nonCompliantCount !== 1 ? 's' : ''}
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>Immediate action required. Review and update compliance records.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.name}
              {tab.count > 0 && (
                <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Compliance Records Tab */}
      {activeTab === 'compliance' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Compliance Records</h2>
            <Link
              to="/legal/compliance/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + New Compliance Record
            </Link>
          </div>

          {complianceLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading compliance records...</p>
            </div>
          ) : complianceRecords.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No compliance records found</p>
              <Link
                to="/legal/compliance/new"
                className="mt-4 inline-block text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Create your first compliance record
              </Link>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {complianceRecords.map((record) => {
                  const isOverdue = record.dueDate && isAfter(new Date(), record.dueDate) && record.status !== ComplianceStatus.compliant;
                  const daysUntilDue = record.dueDate ? differenceInDays(record.dueDate, new Date()) : null;

                  return (
                    <li key={record.id}>
                      <Link
                        to={`/legal/compliance/${record.id}`}
                        className="block hover:bg-gray-50 px-6 py-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <p className="text-sm font-medium text-gray-900">
                                {record.name}
                              </p>
                              <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getComplianceStatusColor(record.status)}`}>
                                {record.status.replace('_', ' ')}
                              </span>
                              <span className="ml-2 text-xs text-gray-500">
                                {record.type.replace('_', ' ')}
                              </span>
                              {isOverdue && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Overdue
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-sm text-gray-500">
                              {record.description}
                              {record.dueDate && (
                                <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
                                  {' • '}
                                  Due: {format(record.dueDate, 'MMM d, yyyy')}
                                  {daysUntilDue !== null && daysUntilDue >= 0 && !isOverdue && ` (${daysUntilDue} days)`}
                                </span>
                              )}
                              {record.responsibleParty && ` • Responsible: ${record.responsibleParty}`}
                            </p>
                          </div>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Policies Tab */}
      {activeTab === 'policies' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Policies</h2>
            <Link
              to="/legal/policies/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + New Policy
            </Link>
          </div>

          {policiesLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading policies...</p>
            </div>
          ) : policies.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No policies found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {policies.map((policy) => (
                  <li key={policy.id}>
                    <Link
                      to={`/legal/policies/${policy.id}`}
                      className="block hover:bg-gray-50 px-6 py-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">
                              {policy.title}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              policy.status === 'active' ? 'bg-green-100 text-green-800' :
                              policy.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {policy.status}
                            </span>
                            <span className="ml-2 text-xs text-gray-500 capitalize">
                              {policy.category}
                            </span>
                            <span className="ml-2 text-xs text-gray-500">
                              v{policy.version}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            Effective: {format(policy.effectiveDate, 'MMM d, yyyy')}
                            {policy.expirationDate && ` • Expires: ${format(policy.expirationDate, 'MMM d, yyyy')}`}
                            {policy.requiresAcknowledgment && ' • Requires Acknowledgment'}
                            {policy.acknowledgments && policy.acknowledgments.length > 0 && ` • ${policy.acknowledgments.length} acknowledged`}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">
                            {policy.createdByName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {format(policy.createdAt, 'MMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Audits Tab */}
      {activeTab === 'audits' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Audits</h2>
            <Link
              to="/legal/audits/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + New Audit
            </Link>
          </div>

          {auditsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading audits...</p>
            </div>
          ) : audits.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No audits found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {audits.map((audit) => (
                  <li key={audit.id}>
                    <Link
                      to={`/legal/audits/${audit.id}`}
                      className="block hover:bg-gray-50 px-6 py-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">
                              {audit.name}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAuditStatusColor(audit.status)}`}>
                              {audit.status.replace('_', ' ')}
                            </span>
                            <span className="ml-2 text-xs text-gray-500 capitalize">
                              {audit.type.replace('_', ' ')}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            Scheduled: {format(audit.scheduledDate, 'MMM d, yyyy')}
                            {audit.startDate && ` • Started: ${format(audit.startDate, 'MMM d, yyyy')}`}
                            {audit.endDate && ` • Completed: ${format(audit.endDate, 'MMM d, yyyy')}`}
                            {audit.findings && audit.findings.length > 0 && ` • ${audit.findings.length} finding${audit.findings.length !== 1 ? 's' : ''}`}
                          </p>
                        </div>
                        <div className="text-right">
                          {audit.conductedByName && (
                            <p className="text-sm text-gray-600">
                              {audit.conductedByName}
                            </p>
                          )}
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Incidents Tab */}
      {activeTab === 'incidents' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Compliance Incidents</h2>
            <Link
              to="/legal/incidents/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + Report Incident
            </Link>
          </div>

          {incidentsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading incidents...</p>
            </div>
          ) : incidents.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No incidents found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {incidents.map((incident) => (
                  <li key={incident.id}>
                    <Link
                      to={`/legal/incidents/${incident.id}`}
                      className="block hover:bg-gray-50 px-6 py-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">
                              {incident.title}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getIncidentSeverityColor(incident.severity)}`}>
                              {incident.severity}
                            </span>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getIncidentStatusColor(incident.status)}`}>
                              {incident.status.replace('_', ' ')}
                            </span>
                            <span className="ml-2 text-xs text-gray-500 capitalize">
                              {incident.type.replace('_', ' ')}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            Reported by {incident.reportedByName} on {format(incident.reportedAt, 'MMM d, yyyy')}
                            {incident.discoveredAt && ` • Discovered: ${format(incident.discoveredAt, 'MMM d, yyyy')}`}
                            {incident.resolvedAt && ` • Resolved: ${format(incident.resolvedAt, 'MMM d, yyyy')}`}
                            {incident.notificationRequired && ' • Notification Required'}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* BAAs Tab */}
      {activeTab === 'baas' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Business Associate Agreements</h2>
            <Link
              to="/legal/baas/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + New BAA
            </Link>
          </div>

          {baasLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading BAAs...</p>
            </div>
          ) : baas.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No Business Associate Agreements found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {baas.map((baa) => {
                  const isExpiring = baa.expirationDate && differenceInDays(baa.expirationDate, new Date()) <= 90 && differenceInDays(baa.expirationDate, new Date()) >= 0;
                  const isExpired = baa.expirationDate && isAfter(new Date(), baa.expirationDate);

                  return (
                    <li key={baa.id}>
                      <Link
                        to={`/legal/baas/${baa.id}`}
                        className="block hover:bg-gray-50 px-6 py-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <p className="text-sm font-medium text-gray-900">
                                {baa.vendorName}
                              </p>
                              <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                baa.status === 'active' ? 'bg-green-100 text-green-800' :
                                baa.status === 'expired' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {baa.status.replace('_', ' ')}
                              </span>
                              {isExpiring && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  Expiring Soon
                                </span>
                              )}
                              {isExpired && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Expired
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-sm text-gray-500">
                              Agreement Date: {format(baa.agreementDate, 'MMM d, yyyy')}
                              {baa.expirationDate && (
                                <span className={isExpired ? 'text-red-600 font-medium' : isExpiring ? 'text-orange-600' : ''}>
                                  {' • '}Expires: {format(baa.expirationDate, 'MMM d, yyyy')}
                                </span>
                              )}
                              {baa.vendorContact && ` • Contact: ${baa.vendorContact}`}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">
                              {baa.signedByName}
                            </p>
                          </div>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Contracts Tab */}
      {activeTab === 'contracts' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Contracts</h2>
            <Link
              to="/contracts/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + New Contract
            </Link>
          </div>

          {contractsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading contracts...</p>
            </div>
          ) : contracts.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No contracts found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {contracts.map((contract) => (
                  <li key={contract.id}>
                    <Link
                      to={`/contracts/${contract.id}`}
                      className="block hover:bg-gray-50 px-6 py-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">
                              {contract.title}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              contract.status === 'active' ? 'bg-green-100 text-green-800' :
                              contract.status === 'expired' ? 'bg-red-100 text-red-800' :
                              contract.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {contract.status}
                            </span>
                            <span className="ml-2 text-xs text-gray-500 capitalize">
                              {contract.type}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            {contract.partyName} • {format(contract.startDate, 'MMM d, yyyy')}
                            {contract.endDate && ` - ${format(contract.endDate, 'MMM d, yyyy')}`}
                            {contract.value > 0 && ` • Value: ${contract.currency} ${contract.value.toLocaleString()}`}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
